import type { ReportLayoutConfig, SectionBuild } from '@/features/reports'

// v3 `configs/CreativePerformance/BenchmarkCreatives.js` (81 ln) — the Creative Watchlist
// Meta tab's layout (v3 `MetaCreativesView.vue` mounts a report page over it; the Google tab
// is a non-report surface and belongs to ORP-18955 with the page host). Port notes:
// - Section `visibleType: 'card'` → `view: 'card'`; `defaultChart: 'column'` → `chartType`;
//   `chartSortMetric: 'cost'` folds into `sortField` (08 seed precedent, creativeDashboard.ts).
// - The `preview_ss_url contains 'orphex'` filter is v3's "has a hosted preview screenshot"
//   gate (the previews live on Orphex storage) — wire data, kept verbatim.
// - v3 `hideCompare` → section `hiddenCompare`; `defaultLevel: 'Creative Level'` → machine id
//   `'creative'`; page-tab `isNotTabCard` has no v4 field and drops (storeAnalysis precedent).
// - v3 `addMetrics` (custom_conversion_1..5) waits for ORP-18643 column shaping; `cardButton`
//   and `fieldValues` are v3 chrome with no v4 field (card affordance rides `allowedViews`).
// - The 'Gram' n-gram section renders a bespoke diverging-bar view in v3 → `pending`
//   (same idiom as 08's own n-gram seeds).
// - `search_value` blobs have no v4 section-level home (module 15 pending).

const AD_LIBRARY_REPORT_ID = 13

const AD_LIBRARY_DIMENSIONS = [
  'preview_ss_url',
  'image_url',
  'library_id',
  'page_id',
  'ad_snapshot_url',
  'publisher_platforms',
  'benchmark',
  'page_name',
  'start_time',
  'stop_time',
  'eu_total_reach',
]

const adLibraryBuild: SectionBuild = () => ({
  id: '13-ad-library',
  reportId: AD_LIBRARY_REPORT_ID,
  scheduleTabs: ['meta_ad_library'],
  preferredDimensions: AD_LIBRARY_DIMENSIONS,
  filters: [{ field: 'preview_ss_url', match_mode: 'contains', value: 'orphex' }],
  view: 'card',
  allowedViews: ['table', 'card', 'chart'],
  chartType: 'column',
  chartDimensions: ['preview_ss_url', 'image_url', 'thumbnail_url'],
  chartMetrics: ['cost', 'impressions', 'cpc', 'cpm'],
  sortField: 'cost',
  tableConfig: { version: 1, defaultSort: null },
})

export const BENCHMARK_CREATIVES: ReportLayoutConfig = [
  {
    name: 'Ad Library',
    labelKey: 'commerceAnalytics.watchlist.adLibrary',
    hideDatePicker: true,
    // v3 `visibleRule` — the whole tab hides unless schedule 13's `meta_ad_library` carries
    // the `preview_ss_url` column (the Meta-tab gate, `MetaCreativesView.vue:49`).
    visibleRule: {
      reportId: AD_LIBRARY_REPORT_ID,
      tabs: ['meta_ad_library'],
      checkCols: ['preview_ss_url'],
    },
    sections: [
      {
        // v3 section id '13-video' (sic — reused from CreativeDashboard's Video section).
        id: '13-video',
        reportId: AD_LIBRARY_REPORT_ID,
        hideChannels: true,
        hiddenCompare: true,
        defaultLevel: 'creative',
        build: adLibraryBuild,
        tabs: [
          {
            name: 'Meta',
            channel: 'meta_ads',
            tabs: ['meta_ad_library'],
            checkCols: ['preview_ss_url'],
          },
        ],
      },
      {
        id: '13-ad-library-gram',
        kind: 'pending',
        pendingNote: 'v3 n-gram renderer (diverging_bar over creative_ngram_data) — unported',
        reportId: AD_LIBRARY_REPORT_ID,
        hideChannels: true,
        tabs: [{ name: 'Gram', tabs: ['creative_ngram_data'] }],
      },
    ],
  },
]
